import React from 'react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { useSidebar } from '../../contexts/SidebarContext';

interface CommonPageHeaderProps {
  title: string;
  subtitle: string;
  icon: React.ComponentType<{ className?: string }>;
  onRefresh?: () => void;
  refreshButtonText?: string;
  children?: React.ReactNode;
  actionButton?: React.ReactNode;
}

/**
 * Egységes oldalfejléc.
 * Design phase 1: a lila gradiens helyett világos, nyugodt felület –
 * a tartalom kapja a hangsúlyt, nem a fejléc.
 */
const CommonPageHeader: React.FC<CommonPageHeaderProps> = ({
  title,
  subtitle,
  icon: Icon,
  onRefresh,
  refreshButtonText = 'Frissítés',
  children,
  actionButton
}) => {
  const { isSidebarCollapsed } = useSidebar();

  const refreshBtn = onRefresh && (
    <button
      onClick={onRefresh}
      className="crm-btn crm-btn-ghost text-sm"
      title={refreshButtonText}
    >
      <ArrowPathIcon className="w-4 h-4" />
      <span className="hidden lg:inline ml-1.5">{refreshButtonText}</span>
    </button>
  );

  return (
    <>
      {/* Desktop - Fixed header */}
      <div
        className={`crm-page-header h-16 items-center px-4 sm:px-6 lg:px-8 fixed top-0 right-0 z-40 hidden md:flex transition-all duration-300 ${
          isSidebarCollapsed ? 'left-16' : 'left-48 lg:left-64'
        }`}
      >
        <div className="flex items-center justify-between w-full gap-4">
          {/* Left side - Title and subtitle */}
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="crm-page-header-icon">
              <Icon className="w-5 h-5" />
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="text-base lg:text-lg font-semibold text-gray-900 dark:text-gray-50 truncate leading-tight">
                {title}
              </h1>
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{subtitle}</p>
            </div>
          </div>

          {/* Right side - Actions */}
          <div className="crm-header-actions flex items-center gap-2 flex-shrink-0">
            {actionButton}
            {refreshBtn}
            {children}
          </div>
        </div>
      </div>

      {/* Mobile - Regular header */}
      <div className="crm-page-header rounded-xl p-3 md:hidden mb-4">
        <div className="space-y-3">
          <div className="flex items-center gap-2.5">
            <div className="crm-page-header-icon">
              <Icon className="w-4 h-4" />
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="text-sm font-semibold text-gray-900 dark:text-gray-50 truncate">{title}</h1>
              <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate">{subtitle}</p>
            </div>
          </div>

          {(actionButton || onRefresh || children) && (
            <div className="crm-header-actions">
              {actionButton}
              {refreshBtn}
              {children}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CommonPageHeader;
